import { Text } from "@mantine/core";
import { Badge, StatusBadge } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { IProcessDetailViewModel } from "@models/extraction";
import { formatDate } from "@utils/formatters";
import "./ProcessInfoSection.scss";

interface ProcessInfoSectionProps {
    processDetail: IProcessDetailViewModel;
    createdBy?: string;
    organisationName?: string;
    doctypeName?: string;
}

export const ProcessInfoSection = ({
    processDetail,
    createdBy,
    organisationName,
    doctypeName,
}: ProcessInfoSectionProps) => {
    const { t } = useTranslation();
    return (
        <div className="process-info">
            <div className="process-info__grid">
                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.processId")}
                    </Text>
                    <Text size="sm" className="process-info__value-mono">
                        {processDetail.id}
                    </Text>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.status")}
                    </Text>
                    <StatusBadge size="sm" status={processDetail.status}>
                        {processDetail.status}
                    </StatusBadge>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.type")}
                    </Text>
                    <Badge size="sm" variant="outline">
                        {processDetail.isBatch ? t("admin.monitoring.processInfo.batch") : t("admin.monitoring.processInfo.single")}
                    </Badge>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.doctype")}
                    </Text>
                    <Text size="sm">{doctypeName || "-"}</Text>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.startedAt")}
                    </Text>
                    <Text size="sm">{formatDate(processDetail.startAt)}</Text>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.processInfo.finishedAt")}
                    </Text>
                    <Text size="sm">{formatDate(processDetail.finishedAt)}</Text>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.costBreakdown.createdBy")}
                    </Text>
                    <Text size="sm">{createdBy || "-"}</Text>
                </div>

                <div className="process-info__field">
                    <Text size="xs" c="dimmed" className="process-info__label">
                        {t("admin.monitoring.costBreakdown.organisation")}
                    </Text>
                    <Text size="sm">{organisationName || "-"}</Text>
                </div>
            </div>

            {(processDetail.userInput.pageNumbers.length > 0 || processDetail.userInput.instructions) && (
                <div className="process-info__user-input">
                    {processDetail.userInput.pageNumbers.length > 0 && (
                        <div className="process-info__field">
                            <Text size="xs" c="dimmed" className="process-info__label">
                                {t("admin.monitoring.processInfo.pages")}
                            </Text>
                            <div className="process-info__page-numbers">
                                {processDetail.userInput.pageNumbers.map((page) => (
                                    <Badge key={page} size="sm" variant="light" color="gray" radius="sm">
                                        {page}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    {processDetail.userInput.instructions && (
                        <div className="process-info__field">
                            <Text size="xs" c="dimmed" className="process-info__label">
                                {t("admin.monitoring.processInfo.instructions")}
                            </Text>
                            <Text size="sm" className="process-info__instructions">
                                {processDetail.userInput.instructions}
                            </Text>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
